import React from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { X, ChevronDown, GripVertical } from "lucide-react";
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FilterValue, FilterConfig } from "../types";

export interface SortableFilterBadgeProps {
  filter: FilterValue;
  isActive: boolean;
  onToggle: () => void;
  onRemove: () => void;
  getLabel: () => string;
  renderOptions: () => React.ReactNode;
  getConfig: () => FilterConfig | undefined;
}

export function SortableFilterBadge({
  filter,
  isActive,
  onToggle,
  onRemove,
  getLabel,
  renderOptions,
  getConfig,
}: SortableFilterBadgeProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: filter.type });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center">
      <Popover open={isActive} onOpenChange={(open) => !open && onToggle()}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={`h-8 px-3 gap-2 border ${
              filter.values.length > 0 
                ? 'bg-purple-P4 text-purple-P1 border-purple-P3 hover:bg-purple-P3' 
                : 'border-input hover:bg-muted'
            }`}
            onClick={onToggle}
          >
            <span
              {...attributes}
              {...listeners}
              className="cursor-grab hover:text-foreground"
              onClick={(e) => e.stopPropagation()}
            >
              <GripVertical className="h-3 w-3" />
            </span>
            <span className="text-sm">{getLabel()}</span>
            <ChevronDown className="h-3 w-3" />
            <span
              className="ml-1 hover:bg-white/20 rounded-full p-0.5 cursor-pointer inline-flex items-center justify-center"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              role="button"
              aria-label="Remove filter"
            >
              <X className="h-3 w-3" />
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3" align="start">
          <div className="space-y-2">
            <div className="font-medium text-sm mb-3">
              {getConfig()?.label || filter.type}
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {renderOptions()}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default SortableFilterBadge;

